import { Injectable, signal, effect } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TabIdService {
  private tabIdMap = signal<Map<string, string>>(new Map());
  private currentActiveTabId = signal<string>('');

  constructor() {
    this.loadFromStorage();

    effect(() => {
      const map = this.tabIdMap();
      this.saveToStorage(map);
    });
  }

  /**
   * Como se escribe el identificador de una pestaña a partir de su RUTA.
   *
   * <p>Es el unico sitio donde se decide esa escritura, y existe para que las partes que la
   * necesitan la obtengan del mismo lugar: la barra nombra sus pestañas con la ruta tal cual
   * -`/layout/accounting/third-party/list`-, el DOM le antepone `tab-` y el formulario guarda lo
   * suyo con la ruta en guiones. Son el mismo sitio escrito de tres maneras.</p>
   *
   * <p>La clave es la de la PANTALLA, no la del modo: hay una pestaña por pantalla, y la misma
   * pestaña pasa de `/list` a `/edit/5` y vuelve. Por eso el modo final -`list`, `create`,
   * `edit`, `view`, `detail`, con su identificador si lo trae- queda fuera. Con el modo dentro,
   * un formulario que nacia en `/edit/5` y una pestaña abierta en `/list` no se reconocian, y
   * quien consultaba se defendia comparando por pedazos: la marca de una pestaña sucia llegaba a
   * todas las que terminaban igual.</p>
   *
   * <p>Lo que la hace fiable es que no consulta NADA: misma pantalla, mismo identificador, sin
   * importar que pestaña estuviera activa antes, que hubiera en el almacenamiento ni en que
   * orden se abrieron las pantallas. Los parametros de consulta y el ancla tambien quedan
   * fuera: `?id=5` no abre otra pestaña, abre la misma.</p>
   *
   * @param ruta ruta de la pantalla, su identificador de pestaña (`tab-/layout/...`) o una clave
   *   ya escrita por este metodo, que sale igual.
   * @returns el identificador de su pestaña.
   */
  claveDeRuta(ruta: string | undefined | null): string {
    if (!ruta) return '';
    let limpia = String(ruta).trim().split('?')[0].split('#')[0];
    try {
      limpia = decodeURIComponent(limpia);
    } catch {
      // Una ruta mal codificada se toma tal cual.
    }
    limpia = limpia.replace(/^tab-(?=[-/])/, '').replace(/tesoreria/gi, 'tesorería');
    // Una clave ya escrita en guiones no se vuelve a recortar: el modo solo se reconoce en la
    // ruta, y recortarla otra vez le quitaria el final a una pantalla que se llame `...-detail`.
    if (limpia.includes('/')) {
      limpia = limpia.replace(/\/+$/, '').replace(TabIdService.MODO_EN_RUTA, '');
    }
    return limpia.replace(/\//g, '-').replace(/[^a-zA-Z0-9-]/g, '');
  }

  /** Modo final de una ruta, con su identificador si lo trae: `/edit/5`, `/list`. */
  private static readonly MODO_EN_RUTA = /\/(list|create|edit|view|detail)(\/[^/]+)?$/;

  /**
   *  Registra un tab con su ID de visualización y su ruta real
   * @param tabId - ID del tab (ej: 'tab--layout-accounting-accounting-concepts-list')
   * @param realId - Ruta real (ej: '/layout/accounting/accounting-concepts/list')
   */
  registerTab(tabId: string, realId: string): void {
    if (!tabId || !realId) return;

    this.tabIdMap.update((currentMap) => {
      const newMap = new Map(currentMap);
      newMap.set(tabId, realId);
      newMap.set(realId, realId);
      return newMap;
    });
  }

  /**
   * Obtiene el ID real de un tab
   * Si el tabId es una ruta, devuelve la ruta misma
   * Si es un ID generado, busca su ruta correspondiente
   */
  getRealTabId(tabId: string): string {
    if (!tabId) return '';
    if (tabId.startsWith('/')) return tabId;
    // Exacto o nada: la busqueda por pedazos devolvia la ruta de OTRA pestaña cuando sus
    // identificadores compartian un trozo.
    return this.tabIdMap().get(tabId) || tabId;
  }

  /**
   *  Busca un tab por su ruta
   */
  findTabByRoute(route: string): string | null {
    if (!route) return null;

    const map = this.tabIdMap();

    // Buscar si la ruta está registrada como valor
    for (const [key, value] of map.entries()) {
      if (value === route || value.includes(route) || route.includes(value)) {
        return key;
      }
    }

    return null;
  }

  setActiveTab(tabId: string): void {
    if (!tabId) return;

    const map = this.tabIdMap();
    if (!map.has(tabId) && !tabId.startsWith('/')) {
      this.registerTab(tabId, tabId);
    }

    this.currentActiveTabId.set(tabId);
    localStorage.setItem('current_tab_id', tabId);
  }

  getActiveTab(): string {
    return this.currentActiveTabId();
  }

  getTabId(): string {
    const activeTab = this.currentActiveTabId();
    if (activeTab) {
      const realId = this.getRealTabId(activeTab);
      return realId;
    }
    return '';
  }

  setTabId(tabId: string): void {
    this.setActiveTab(tabId);
  }

  clearTab(tabId: string): void {
    if (!tabId) return;

    this.tabIdMap.update((currentMap) => {
      const newMap = new Map(currentMap);
      newMap.delete(tabId);

      for (const [key, value] of newMap.entries()) {
        if (key === tabId || value === tabId || value.includes(tabId)) {
          newMap.delete(key);
        }
      }

      return newMap;
    });

    if (this.currentActiveTabId() === tabId) {
      this.currentActiveTabId.set('');
      localStorage.removeItem('current_tab_id');
    }
  }

  clearAll(): void {
    this.tabIdMap.set(new Map());
    this.currentActiveTabId.set('');
    localStorage.removeItem('current_tab_id');
  }

  getTabIdSignal() {
    return this.currentActiveTabId;
  }

  clear(): void {
    this.clearAll();
  }

  private saveToStorage(map: Map<string, string>): void {
    const data = Array.from(map.entries());
    localStorage.setItem('tab_id_map', JSON.stringify(data));
  }

  private loadFromStorage(): void {
    const storedMap = localStorage.getItem('tab_id_map');
    if (storedMap) {
      try {
        const data = JSON.parse(storedMap) as [string, string][];
        const map = new Map(data);
        this.tabIdMap.set(map);
      } catch (e) {
        console.warn('[TabIdService] Error al cargar del storage:', e);
      }
    }

    const activeTab = localStorage.getItem('current_tab_id');
    if (activeTab) {
      this.currentActiveTabId.set(activeTab);
    }
  }
}
